import { SolTagColor } from '@sol/components';

export type EngagementRequestStatus =
  | 'DRAFT'
  | 'NEW'
  | 'IN_REVIEW'
  | 'MISSING_INFORMATION'
  | 'SCHEDULED'
  | 'ACCEPTED'
  | 'STARTED'
  | 'COMPLETED'
  | 'CANCELLED';

export interface EngagementRequestStatusConfig {
  readonly label: string;
  readonly color: SolTagColor;
}

export const ENGAGEMENT_REQUEST_STATUS_CONFIG: Record<EngagementRequestStatus, EngagementRequestStatusConfig> = {
  DRAFT: { label: 'asm-engagement-request.status.draft', color: 'grey' },
  NEW: { label: 'asm-engagement-request.status.new', color: 'blue' },
  IN_REVIEW: { label: 'asm-engagement-request.status.in-review', color: 'cyan' },
  MISSING_INFORMATION: { label: 'asm-engagement-request.status.missing-information', color: 'red' },
  SCHEDULED: { label: 'asm-engagement-request.status.scheduled', color: 'green' },
  ACCEPTED: { label: 'asm-engagement-request.status.accepted', color: 'green' },
  STARTED: { label: 'asm-engagement-request.status.started', color: 'blue' },
  COMPLETED: { label: 'asm-engagement-request.status.completed', color: 'grey' },
  CANCELLED: { label: 'asm-engagement-request.status.cancelled', color: 'grey' },
};

export type EngagementRequestWorkflowFilter =
  'all' | 'action-required' | 'drafts' | 'requests' | 'scheduled' | 'ongoing' | 'completed';

export const WORKFLOW_FILTER_BUCKET: Partial<Record<EngagementRequestWorkflowFilter, string>> = {
  drafts: 'Drafts',
  requests: 'Requests',
  scheduled: 'Scheduled',
  ongoing: 'Ongoing',
  completed: 'Completed',
};

export const WORKFLOW_FILTER_COLOR: Record<EngagementRequestWorkflowFilter, SolTagColor> = {
  all: 'grey',
  'action-required': 'orange',
  drafts: 'yellow',
  requests: 'blue',
  scheduled: 'green',
  ongoing: 'cyan',
  completed: 'grey',
};

export type EngagementRequestSubscriptionType = string;

export interface EngagementRequestSubscriptionConfig {
  readonly label: string;
  readonly color: SolTagColor;
}

export const SUBSCRIPTION_TYPE_CONFIG: Record<string, EngagementRequestSubscriptionConfig> = {
  SNAPSHOT: { label: 'asm-engagement-request.subscription.snapshot', color: 'grey' },
  SNAPSHOT_API: { label: 'asm-engagement-request.subscription.snapshot-api', color: 'grey' },
  SNAPSHOT_MOBILE: { label: 'asm-engagement-request.subscription.snapshot-mobile', color: 'grey' },
  ASSURE: { label: 'asm-engagement-request.subscription.assure', color: 'grey' },
  SWAT: { label: 'asm-engagement-request.subscription.swat', color: 'grey' },
  DAST_EXPERT: { label: 'asm-engagement-request.subscription.dast-expert', color: 'grey' },
};

export interface EngagementTypePrice {
  readonly amount: number | undefined;
  readonly unit: string | undefined;
  readonly isCents: boolean;
}

export interface EngagementType {
  readonly id: string;
  readonly name: string;
  readonly subscriptionType: EngagementRequestSubscriptionType;
  readonly description: string;
  readonly price: EngagementTypePrice;
}

export interface SubscriptionCostUnitDto {
  readonly id: string;
  readonly name: string | null;
  readonly display_name: string | null;
  readonly is_cents: boolean | null;
  readonly unit_mode: string | null;
}

export interface SubscriptionCostItemDto {
  readonly subscription_type: string;
  readonly module: string;
  readonly action: string;
  readonly allowed: boolean | null;
  readonly cost: number | null;
  readonly unit: SubscriptionCostUnitDto | null;
  readonly detail: string | null;
}

export interface EngagementRequest {
  readonly id: number;
  readonly status: EngagementRequestStatus;
  readonly subscriptionType: EngagementRequestSubscriptionType;
  readonly applicationName: string;
  readonly assetGroupName: string | undefined;
  readonly created: Date;
  readonly updated: Date;
  readonly startDate: Date | undefined;
  readonly endDate: Date | undefined;
  readonly commentsCount: number | undefined;
  readonly newCommentsCount: number | undefined;
  readonly commentPendingSince: Date | undefined;
  readonly focusArea: string | undefined;
  readonly includeExecutiveSummary: boolean;
  readonly mainUrl: string;
  readonly technicalContactName: string;
  readonly technicalContactEmail: string;
  readonly preferredStartDate: string | null;
  readonly applicationServices: string | undefined;
  readonly accounts: readonly EngagementRequestAccount[];
  /** Display name of the user who created the draft. Always present (may be a
   *  blank string when the user has no name set — there is no email fallback). */
  readonly createdBy: string;
  /** Display name of the user who submitted the request to the pentest platform.
   *  `undefined` for drafts not yet submitted. */
  readonly submittedBy: string | undefined;
}

export interface EngagementRequestDto {
  readonly id: number;
  readonly status: EngagementRequestStatus;
  readonly subscription_type: EngagementRequestSubscriptionType;
  readonly application_name: string;
  readonly asset_group_name: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly start_date: string | null;
  readonly end_date: string | null;
  readonly comments_count: number | null;
  readonly new_comments_count: number | null;
  readonly comment_pending_since: string | null;
  readonly focus_area: string | null;
  readonly include_executive_summary: boolean;
  readonly main_url: string;
  readonly technical_contact_name: string;
  readonly technical_contact_email: string;
  readonly preferred_start_date: string | null;
  readonly application_services: string | null;
  readonly accounts: readonly AccountDto[];
  /** Full name of the creator — always present (may be blank). */
  readonly created_by: string;
  /** Full name of the submitter, or `null` for drafts not yet submitted. */
  readonly submitted_by: string | null;
}

/** Pagination envelope returned by the backend `DefaultResultsSetPagination`:
 *  `{ items, page: { total, number, size } }`. The count lives under
 *  `page.total` (not a top-level `total`). */
export interface EngagementRequestListPageDto {
  readonly total: number;
  readonly number: number;
  readonly size: number;
}

export interface EngagementRequestListResponseDto {
  readonly items: readonly EngagementRequestDto[];
  readonly page: EngagementRequestListPageDto;
}

export type AssetType = 'domain' | 'host' | 'website';

export interface AssetItem {
  readonly id: number;
  readonly label: string;
  readonly type: AssetType;
}

export interface AssetItemDto {
  readonly id: number;
  readonly label: string;
  readonly type: AssetType;
}

export interface AssetSearchResponseDto {
  readonly items: readonly AssetItemDto[];
  readonly total: number;
}

export type EngagementAssetType = Extract<AssetType, 'domain' | 'host' | 'website'>;

export interface HostDto {
  readonly id: number;
  readonly key: string;
  readonly ip_address: string;
}

export interface WebsiteDto {
  readonly id: number;
  readonly url: string;
  readonly legacy_id: number | null;
}

export interface DomainDto {
  readonly id: number;
  readonly key: string;
  readonly domain: string;
}

export interface HostListResponseDto {
  readonly items: readonly HostDto[];
  readonly page: EngagementRequestListPageDto;
}

export interface WebsiteListResponseDto {
  readonly items: readonly WebsiteDto[];
  readonly page: EngagementRequestListPageDto;
}

export interface DomainListResponseDto {
  readonly items: readonly DomainDto[];
  readonly page: EngagementRequestListPageDto;
}

export type CredentialPresetType = 'username-password' | 'access-key-secret' | 'api-key' | 'custom';

export interface CredentialClass {
  readonly id: number;
  readonly name: string;
  readonly hidden: boolean;
}

export interface CredentialClassDto {
  readonly id: number;
  readonly name: string;
  readonly hidden: boolean;
}

export interface CredentialEntry {
  readonly classId: number | undefined;
  readonly className: string;
  readonly value: string;
  readonly hidden: boolean;
  readonly createdId: number | undefined;
  readonly rowIndex: number;
}

export interface Account {
  readonly id: number;
  readonly name: string;
}

export interface AccountListItem extends Account {
  readonly type: string;
  readonly role: string | undefined;
  readonly url: string | undefined;
  readonly subscopeId: number | undefined;
  readonly createdByGhostLabs: boolean;
  readonly editable: boolean;
}

export interface AccountDto {
  readonly id: number;
  readonly vm_id: number | null;
  readonly name: string;
  readonly type: string;
  readonly role: string | null;
  readonly url: string | null;
  readonly created_by_ghost_labs: boolean;
  readonly editable: boolean;
}

export interface EngagementRequestAccount {
  readonly id: number;
  readonly name: string;
  readonly type: string;
  readonly role: string | undefined;
  readonly url: string | undefined;
  readonly editable: boolean;
}

export interface AccountListItemDto {
  readonly id: number;
  readonly name: string;
  readonly type: string;
  readonly role: string | null;
  readonly url: string | null;
  readonly subscope_id: number | null;
  readonly created_by_ghost_labs: boolean;
  readonly editable: boolean;
}

export interface AccountListResponseDto {
  readonly items: readonly AccountListItemDto[];
  readonly page: EngagementRequestListPageDto;
}

export interface EngagementUsageReference {
  readonly engagementRequestId: number | undefined;
  readonly vmId: number | undefined;
  readonly applicationName: string;
  readonly status: string;
  readonly subscriptionType: string;
  readonly mainUrl: string;
  readonly preferredStartDate: Date | undefined;
  readonly createdAt: Date | undefined;
}

export interface EngagementUsageReferenceDto {
  readonly engagement_request_id: number | null;
  readonly vm_id: number | null;
  readonly application_name: string;
  readonly status: string;
  readonly subscription_type: string;
  readonly main_url: string;
  readonly preferred_start_date: string | null;
  readonly created_at: string | null;
}

export interface AccountUsage {
  readonly live: readonly EngagementUsageReference[];
  readonly historical: readonly EngagementUsageReference[];
}

export interface AccountUsageDto {
  readonly live: readonly EngagementUsageReferenceDto[];
  readonly historical: readonly EngagementUsageReferenceDto[];
}

export interface Credential {
  readonly id: number;
  readonly vmId: number | undefined;
  readonly accountId: number;
  readonly classId: number;
  readonly className: string;
  readonly hidden: boolean;
  readonly value: string | undefined;
  readonly hasValue: boolean;
  readonly editable: boolean;
  readonly managed: boolean;
}

export interface CredentialDto {
  readonly id: number;
  readonly vm_id: number | null;
  readonly account_id: number;
  readonly class_id: number;
  readonly class_name: string;
  readonly hidden: boolean;
  readonly value?: string;
  readonly has_value: boolean;
  readonly editable: boolean;
  readonly managed: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AccountDetail extends AccountListItem {
  readonly vmId: number;
  readonly status: string | undefined;
  readonly statusComment: string | undefined;
  readonly managed: boolean;
  readonly createdByGhostLabs: boolean;
  readonly editable: boolean;
  readonly credentials: readonly Credential[];
  readonly usedBy: AccountUsage;
}

export interface AccountDetailDto extends AccountListItemDto {
  readonly vm_id: number;
  readonly status: string | null;
  readonly status_comment: string | null;
  readonly managed: boolean;
  readonly created_by_ghost_labs: boolean;
  readonly editable: boolean;
  readonly credentials: readonly CredentialDto[];
  readonly used_by: AccountUsageDto;
}

export interface CredentialUpdateRequestDto {
  readonly value: string;
}

export interface AccountWriteRequestDto {
  readonly name: string;
  readonly type: string;
  readonly role?: string;
  readonly url?: string;
}

export interface AccountCreateResponseDto {
  readonly id: number;
  readonly name: string;
  readonly type: string;
  readonly role: string;
  readonly url: string;
}

export interface CreatedAccount {
  readonly accountId: number;
}

export interface CredentialCreateRequestDto {
  readonly account_id: number;
  readonly class_id: number;
  readonly value: string;
}

export type CredentialCreateResponseDto = CredentialDto;

export interface CreatedCredential {
  readonly credentialId: number;
  readonly vmId: number | undefined;
}

export interface Finding {
  /** Stable observation identifier (the observation `key`), used to deep-link
   *  to the finding's tracker detail page. */
  readonly observationId: string;
  readonly score: string;
  readonly status: string | undefined;
  readonly asset: string;
  readonly category: string;
  readonly value: string;
  readonly discovered: Date;
}

export interface FindingDto {
  readonly _id: string;
  readonly priority: string;
  readonly category: string;
  readonly subcategory: string;
  readonly value: string;
  readonly asset: string;
  readonly updated: string;
  readonly description: string;
  readonly discovered: string;
  readonly dismiss: boolean;
  readonly score: string;
  readonly status: string | null;
  readonly previous_value: string | null;
  readonly last_scan: string | null;
}

export interface FindingListResponseDto {
  readonly items: readonly FindingDto[];
  readonly page: EngagementRequestListPageDto;
}

export interface CommentAuthor {
  readonly name: string;
  readonly isStaff: boolean;
}

export interface Comment {
  readonly id: string;
  readonly author: CommentAuthor;
  readonly text: string;
  readonly createdAt: Date;
  readonly parentId: string | undefined;
}

export interface CommentThreadReplyDto {
  readonly id: number;
  readonly parent_id: number | null;
  readonly author: string;
  readonly author_is_staff: boolean;
  readonly created: string;
  readonly text: string;
}

export interface CommentThreadDto {
  readonly id: number;
  readonly author: string;
  readonly author_is_staff: boolean;
  readonly created: string;
  readonly text: string;
  readonly support_status: string;
  readonly replies: readonly CommentThreadReplyDto[];
}

export interface CommentListResponseDto {
  readonly threads: readonly CommentThreadDto[];
}

export interface CommentResponseAuthorDto {
  readonly id: number;
  readonly email: string;
  readonly full_name: string;
  readonly is_staff: boolean;
}

export interface CommentResponseDto {
  readonly id: number;
  readonly vm_id: number | null;
  readonly comment: string;
  readonly support_status: string;
  readonly author: CommentResponseAuthorDto | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export type SupportStatus = 'NOT_APPLICABLE' | 'WAITING' | 'DONE' | 'INTERNAL';

export interface CreateCommentPayload {
  readonly text: string;
  readonly parentId: string | undefined;
  readonly supportStatus?: SupportStatus;
}

export interface CreateCommentPayloadDto {
  readonly text: string;
  readonly parent_id: number | null;
  readonly support_status?: SupportStatus;
}

/** A top-level comment together with its replies (max one level deep). */
export interface CommentThread {
  readonly root: Comment;
  readonly replies: readonly Comment[];
  readonly replyCount: number;
}

export interface CreateEngagementRequestPayload {
  readonly status: 'DRAFT' | 'NEW';
  readonly subscriptionType: EngagementRequestSubscriptionType;
  readonly applicationName: string;
  readonly mainUrl: string;
  readonly focusArea: string;
  readonly includeExecutiveSummary: boolean;
  readonly applicationServices: string;
  readonly technicalContactName: string;
  readonly technicalContactEmail: string;
  readonly preferredStartDate: string;
  readonly accountIds: readonly number[];
}

export interface CreateEngagementRequestPayloadDto {
  readonly status: 'DRAFT' | 'NEW';
  readonly subscription_type: EngagementRequestSubscriptionType;
  readonly application_name: string;
  readonly main_url: string;
  readonly focus_area: string;
  readonly include_executive_summary: boolean;
  readonly application_services: string;
  readonly technical_contact_name: string;
  readonly technical_contact_email: string;
  readonly preferred_start_date: string;
  readonly account_ids: readonly number[];
}
